/**
 * Remembering which session slot this device holds.
 *
 * A slot is claimed once and proven with a token. Without this, closing the tab to paste the
 * link into WhatsApp and coming back would look like a third participant and be refused.
 *
 * What is stored is a session code, a slot letter, a token and an optional first name. Never a
 * coordinate, and every entry is dropped the moment its session expires.
 */

import type { ParticipantId } from '@beeline/shared';

const PREFIX = 'beeline:session:';

export interface Membership {
  code: string;
  participant: ParticipantId;
  token: string;
  name: string | null;
  /** Epoch milliseconds. Entries past this are swept on every read. */
  expiresAt: number;
  /**
   * True when this device created the session rather than following a link into it.
   *
   * It decides what happens first: a host with an unsent link gets the share screen, and anyone
   * who arrived by link gets the permission taps, because they are about to walk.
   */
  created?: boolean;
}

const keyFor = (code: string): string => `${PREFIX}${code}`;

const storage = (): Storage | null => {
  try {
    // Private browsing and locked-down settings can make this throw rather than return null.
    return window.localStorage;
  } catch {
    return null;
  }
};

export function rememberMembership(membership: Membership): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(keyFor(membership.code), JSON.stringify(membership));
  } catch {
    // A full or disabled store costs the user a rejoin, nothing worse.
  }
}

export function recallMembership(code: string): Membership | null {
  const store = storage();
  if (!store) return null;

  const raw = store.getItem(keyFor(code));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Membership;
    if (typeof parsed?.token !== 'string' || typeof parsed?.expiresAt !== 'number') return null;
    if (parsed.expiresAt <= Date.now()) {
      store.removeItem(keyFor(code));
      return null;
    }
    return parsed;
  } catch {
    store.removeItem(keyFor(code));
    return null;
  }
}

export function forgetMembership(code: string): void {
  storage()?.removeItem(keyFor(code));
}

/** Update the stored expiry after an extension, so a later reload still trusts the entry. */
export function refreshMembershipExpiry(code: string, expiresAt: number): void {
  const existing = recallMembership(code);
  if (existing) rememberMembership({ ...existing, expiresAt });
}

/**
 * Drop every expired entry. Called at startup: nothing about a finished session should linger
 * on the device, and this is the only place that could accumulate.
 */
export function pruneMemberships(now: number = Date.now()): number {
  const store = storage();
  if (!store) return 0;

  const stale: string[] = [];
  for (let index = 0; index < store.length; index++) {
    const key = store.key(index);
    if (!key?.startsWith(PREFIX)) continue;

    try {
      const parsed = JSON.parse(store.getItem(key) ?? 'null') as Membership | null;
      if (!parsed || typeof parsed.expiresAt !== 'number' || parsed.expiresAt <= now) {
        stale.push(key);
      }
    } catch {
      stale.push(key);
    }
  }

  for (const key of stale) store.removeItem(key);
  return stale.length;
}
